import { displayVersion, type GoalDto } from "@home/shared";
import { useState, type ReactNode } from "react";
import { api } from "../api.ts";
import { AccountsSheet } from "../components/AccountsSheet.tsx";
import { FamilySheet } from "../components/FamilySheet.tsx";
import { ContributeSheet, GoalList, NewGoalSheet } from "../components/Goals.tsx";
import { HelpSheet } from "../components/HelpSheet.tsx";
import { HomeItemsSheet } from "../components/HomeItems.tsx";
import { FridgeSheet, MenuSheet } from "../components/Fridge.tsx";
import { PocketKidsSheet } from "../components/Pocket.tsx";
import { IncomeSheet } from "../components/Setup.tsx";
import { RecurringSheet } from "../components/RecurringSheet.tsx";
import { Segmented } from "../components/Segmented.tsx";
import { PrimaryAction, Sheet } from "../components/Sheet.tsx";
import { toast } from "../components/Toast.tsx";
import { EmptyState } from "../components/EmptyState.tsx";
import { t, type Lang } from "../i18n.ts";
import { errorText, useMeta } from "../meta.ts";
import { useSync } from "../store.ts";
import { hapticSelect, openBotChat } from "../telegram.ts";
import { invalidateRemote, useRemote } from "../useRemote.ts";

export type SheetName = "goals" | "pocket" | "fridge" | "menu" | "kit" | "warranties" | "recurring" | "accounts" | "family" | "help" | "income";

interface Props {
  lang: Lang;
  /** Лист, открытый сразу (ссылка из бота: ?screen=kit). */
  initialSheet?: SheetName | null;
  onLang: (lang: Lang) => void;
  onAdmin: () => void;
  onDebts: () => void;
  onReport: () => void;
  /** Открыть список покупок (меню на неделю ушло в список). */
  onOpenList: (id: string) => void;
}

/** «Ещё»: отчёт, копилки, платежи, счета, кредиты, семья, язык, помощь и (для разработчика) панель. */
export function MorePage({ lang, initialSheet = null, onLang, onAdmin, onDebts, onReport, onOpenList }: Props) {
  const { me } = useSync();
  const meta = useMeta();
  const [sheet, setSheet] = useState<SheetName | null>(initialSheet);
  const adult = me?.role !== "child";
  const myName = meta.data?.members.find((m) => m.isMe)?.name ?? me?.members.find((m) => m.userId === me.userId)?.displayName ?? "";
  const bot = meta.data?.botUsername ?? null;

  const changeLang = async (next: Lang) => {
    onLang(next);
    try {
      await api.setLanguage(next);
      invalidateRemote();
    } catch (err) {
      toast(errorText(err));
    }
  };

  const open = (name: SheetName) => {
    hapticSelect();
    setSheet(name);
  };

  return (
    <main className="dash">
      <section className="profile">
        <div className="avatar" aria-hidden="true">
          {(myName || "?").slice(0, 1).toUpperCase()}
        </div>
        <div>
          <div className="profile-name">{myName}</div>
          <div className="muted">
            {me?.familyName}
            {me && ` · ${t(`role.${me.role}`)}`}
          </div>
        </div>
      </section>

      {adult && (
        <Group title={t("sectionMoney")}>
          <Row icon="📊" title={t("reportMonth")} onClick={onReport} />
          <Row icon="🐷" title={t("goalsTab")} onClick={() => open("goals")} />
          <Row icon="🧒" title={t("pocketMoney")} onClick={() => open("pocket")} />
          <Row icon="🔔" title={t("recurringTitle")} onClick={() => open("recurring")} />
          <Row icon="💳" title={t("accountsTitle")} onClick={() => open("accounts")} />
          <Row icon="🏦" title={t("debtsTitle")} onClick={onDebts} />
          <Row icon="💼" title={t("incomeTitle")} onClick={() => open("income")} />
        </Group>
      )}

      {adult && (
        <Group title={t("sectionHome")}>
          <Row icon="🧊" title={t("fridgeTitle")} onClick={() => open("fridge")} />
          <Row icon="🍽" title={t("menuTitle")} onClick={() => open("menu")} />
          <Row icon="💊" title={t("kitTitle")} onClick={() => open("kit")} />
          <Row icon="🛡" title={t("warrantiesTitle")} onClick={() => open("warranties")} />
        </Group>
      )}

      <Group title={t("sectionFamily")}>
        <Row icon="👨‍👩‍👧" title={t("familyTitle")} meta={me ? t("membersN", { n: me.members.length }) : undefined} onClick={() => open("family")} />
        <li className="row">
          <span className="row-icon" aria-hidden="true">
            🌐
          </span>
          <div className="row-body">
            <div className="row-title">{t("language")}</div>
          </div>
          <div className="row-end lang-switch">
            <Segmented
              value={lang}
              onChange={(l) => void changeLang(l)}
              label={t("language")}
              options={[
                { value: "ru", label: "RU" },
                { value: "uz", label: "UZ" },
              ]}
            />
          </div>
        </li>
        {bot && <Row icon="💬" title={t("openBot")} meta={`@${bot}`} onClick={() => openBotChat(bot)} />}
        <Row icon="❓" title={t("help")} onClick={() => open("help")} />
      </Group>

      {me?.isDeveloper && (
        <Group title="Dev">
          <Row icon="🛠" title={t("devPanel")} onClick={onAdmin} />
        </Group>
      )}

      <VersionNote server={meta.data?.version ?? null} />

      {sheet === "goals" && <GoalsSheet lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "pocket" && <PocketKidsSheet lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "fridge" && <FridgeSheet lang={lang} onClose={() => setSheet(null)} onOpenList={onOpenList} />}
      {sheet === "menu" && <MenuSheet lang={lang} onClose={() => setSheet(null)} onOpenList={onOpenList} />}
      {sheet === "kit" && <HomeItemsSheet kind="medicine" lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "warranties" && <HomeItemsSheet kind="warranty" lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "recurring" && <RecurringSheet lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "accounts" && adult && <AccountsSheet lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "family" && <FamilySheet onClose={() => setSheet(null)} />}
      {sheet === "income" && <IncomeSheet lang={lang} onClose={() => setSheet(null)} />}
      {sheet === "help" && <HelpSheet child={!adult} onClose={() => setSheet(null)} />}
    </main>
  );
}

/**
 * Версия внизу «Ещё»: видно, что после обновления сервера работает новый код. Сервер уже новее этой копии —
 * Telegram показал сохранённую старую: подсказываем закрыть и открыть приложение заново.
 */
function VersionNote({ server }: { server: string | null }) {
  const app = displayVersion(__APP_VERSION__);
  const stale = server !== null && server !== "dev" && app !== "dev" && server !== app;
  return (
    <p className="hint version-note">
      {t("versionLabel", { v: app })}
      {stale && (
        <>
          <br />
          {t("versionStale", { v: server })}
        </>
      )}
    </p>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <div className="group-title">{title}</div>
      <ul className="row-list group">{children}</ul>
    </section>
  );
}

function Row({ icon, title, meta, onClick }: { icon: string; title: string; meta?: string; onClick: () => void }) {
  return (
    <li>
      <button type="button" className="row row-btn" onClick={onClick}>
        <span className="row-icon" aria-hidden="true">
          {icon}
        </span>
        <div className="row-body">
          <div className="row-title">{title}</div>
          {meta && <div className="row-meta">{meta}</div>}
        </div>
        <span className="chevron" aria-hidden="true">
          ›
        </span>
      </button>
    </li>
  );
}

function GoalsSheet({ lang, onClose }: { lang: Lang; onClose: () => void }) {
  const { me } = useSync();
  const remote = useRemote("goals", () => api.goals().then((r) => r.goals));
  const [creating, setCreating] = useState(false);
  const [picked, setPicked] = useState<GoalDto | null>(null);
  const goals = remote.data ?? [];
  return (
    <Sheet title={`🐷 ${t("goalsTab")}`} onClose={onClose}>
      {!remote.data && remote.loading ? (
        <div className="skeleton skeleton-block" />
      ) : goals.length === 0 ? (
        <EmptyState icon="🐷" title={t("noGoalsYet")} text={me?.role === "child" ? t("noGoalsChild") : t("noGoalsHint")} />
      ) : (
        <GoalList goals={goals} lang={lang} onPick={setPicked} />
      )}
      <PrimaryAction label={`＋ ${t("newGoal")}`} onClick={() => setCreating(true)} />
      {creating && <NewGoalSheet lang={lang} onClose={() => setCreating(false)} />}
      {picked && <ContributeSheet goal={picked} lang={lang} onClose={() => setPicked(null)} />}
    </Sheet>
  );
}
